
import { useState } from "react";
import { Pencil, Check, X, Heart, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { supabaseExtended } from "@/integrations/supabase/extendedClient";
import { UserProfile } from "@/types/profile";
import { captureException } from "@/utils/sentry";

interface ProfileHeaderProps {
  profile: UserProfile | null;
  isOwnProfile: boolean;
  shortAddress: string;
  fullAddress: string;
  coinCount: number;
  address?: string;
}

const ProfileHeader = ({ 
  profile, 
  isOwnProfile, 
  shortAddress, 
  fullAddress, 
  coinCount,
  address 
}: ProfileHeaderProps) => {
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [displayName, setDisplayName] = useState(profile?.username || shortAddress);

  const handleSaveDisplayName = async () => {
    if (!profile || !isOwnProfile) return;
    
    try {
      const { error } = await supabaseExtended
        .from('profiles')
        .update({ username: displayName })
        .eq('wallet_address', address?.toLowerCase());
        
      if (error) {
        console.error("Error updating display name:", error);
        captureException(error, {
          component: "ProfileHeader",
          operation: "updateDisplayName",
          address: address,
          previousName: profile.username,
          newName: displayName,
          errorCode: error.code, 
          errorDetails: error.details
        });
        
        toast({
          title: "Error",
          description: "Failed to update display name",
          variant: "destructive",
        });
        return;
      }
      
      toast({
        title: "Success",
        description: "Display name updated successfully",
      });
      
      // Update local state to reflect the change
      if (profile) {
        profile.username = displayName;
      }
      setIsEditing(false);
    } catch (error) {
      console.error("Error saving display name:", error);
      captureException(error, {
        component: "ProfileHeader",
        operation: "saveDisplayName",
        address: address,
        displayName: displayName
      });
      
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive",
      });
    }
  };

  const cancelEditing = () => {
    setDisplayName(profile?.username || shortAddress);
    setIsEditing(false);
  };

  return (
    <div className="rounded-lg bg-card p-6 mb-6">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          {isEditing ? (
            <div className="flex items-center gap-2">
              <Input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="max-w-[200px]"
                autoFocus
              />
              <Button 
                size="icon" 
                variant="ghost" 
                onClick={handleSaveDisplayName}
                type="button"
                aria-label="Save display name"
              >
                <Check className="h-4 w-4" />
              </Button>
              <Button 
                size="icon" 
                variant="ghost" 
                onClick={cancelEditing}
                type="button"
                aria-label="Cancel editing"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-bold">{profile?.username || shortAddress}</h1>
              {isOwnProfile && profile?.id && (
                <button 
                  className="text-muted-foreground hover:text-foreground"
                  onClick={() => setIsEditing(true)}
                  aria-label="Edit display name"
                >
                  <Pencil className="h-4 w-4" />
                </button>
              )}
            </>
          )}
        </div>
        <div className="text-sm text-muted-foreground bg-secondary/50 px-3 py-1 rounded-full">
          {coinCount} {coinCount === 1 ? 'coin' : 'coins'} created
        </div>
      </div>
      
      <div className="text-sm text-primary font-mono mb-6">
        {fullAddress}
      </div>

      <div className="flex gap-4 text-sm text-muted-foreground">
        <div className="flex items-center gap-1">
          <Heart className="h-4 w-4" />
          <span>0</span>
        </div>
        <div className="flex items-center gap-1">
          <MessageSquare className="h-4 w-4" />
          <span>0</span>
        </div>
      </div>
    </div>
  );
};

export default ProfileHeader;
